import { create } from "zustand";

/**
 * Shared entry point for the credit gate modals.
 *
 * Opens only for a 402 on submit or a worker failure before the charge. A completed
 * task that went negative is not a gate. Surfaces that open it do not also toast.
 */

export type CreditGateErrorCode = "INSUFFICIENT_CREDITS";

export type CreditGateRequiredAction =
  | "add_credits_or_subscribe"
  | "ask_company_admin_to_allocate";

export type CreditGateSurface =
  | "meeting"
  | "meeting_processing"
  | "meeting_bot"
  | "agent"
  | "artifact";

export type CreditGateRequest = {
  errorCode: CreditGateErrorCode;
  context?: CreditGateSurface;
  balance?: string | number | null;
  user_effective_available?: string | number | null;
  required_action?: CreditGateRequiredAction;
};

type CreditGateState = {
  request: CreditGateRequest | null;
  openCreditGateModal: (request: CreditGateRequest) => void;
  closeCreditGateModal: () => void;
};

export const useCreditGateStore = create<CreditGateState>((set) => ({
  request: null,
  openCreditGateModal: (request) => set({ request }),
  closeCreditGateModal: () => set({ request: null }),
}));

const CREDIT_GATE_CODES: readonly string[] = ["INSUFFICIENT_CREDITS"];

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/**
 * The credit code an error carries, or null when it is not a gate.
 *
 * Takes the whole error: depending on transport the code arrives bare, as
 * `error_code`/`errorCode`, or under `response.data.detail`.
 */
export const creditGateErrorCode = (
  source: unknown,
): CreditGateErrorCode | null => {
  if (typeof source === "string") {
    return CREDIT_GATE_CODES.includes(source)
      ? (source as CreditGateErrorCode)
      : null;
  }
  if (typeof source !== "object" || source === null) return null;

  const record = source as Record<string, any>;
  const candidates = [
    record.error_code,
    record.errorCode,
    record.response?.data?.detailPayload?.error_code,
    record.response?.data?.detail?.error_code,
    record.response?.data?.error_code,
    record.detail?.error_code,
    record.detailPayload?.error_code,
  ];
  for (const candidate of candidates) {
    if (typeof candidate === "string") {
      const code = creditGateErrorCode(candidate);
      if (code) return code;
    }
  }
  return null;
};

export const isCreditGateError = (source: unknown): boolean =>
  creditGateErrorCode(source) !== null;

const readDetailRecord = (source: unknown): Record<string, unknown> | null => {
  if (!isRecord(source)) return null;
  if (isRecord(source.detailPayload)) return source.detailPayload;
  if (isRecord(source.detail)) return source.detail;
  const nested = (source as Record<string, unknown>).response?.data;
  if (isRecord(nested) && isRecord(nested.detailPayload)) return nested.detailPayload;
  if (isRecord(nested) && isRecord(nested.detail)) return nested.detail;
  return source;
};

export const creditGateFieldsFromDetail = (
  source: unknown,
): Pick<
  CreditGateRequest,
  "balance" | "user_effective_available" | "required_action"
> => {
  const detail = readDetailRecord(source);
  if (!detail) return {};

  const requiredAction = detail.required_action;
  return {
    balance: detail.balance as string | number | null | undefined,
    user_effective_available: detail.user_effective_available as
      | string
      | number
      | null
      | undefined,
    required_action:
      requiredAction === "add_credits_or_subscribe" ||
      requiredAction === "ask_company_admin_to_allocate"
        ? requiredAction
        : undefined,
  };
};

export const buildCreditGateRequest = (
  source: unknown,
  context?: CreditGateSurface,
): CreditGateRequest | null => {
  if (!creditGateErrorCode(source)) return null;
  return {
    errorCode: "INSUFFICIENT_CREDITS",
    context,
    ...creditGateFieldsFromDetail(source),
  };
};

export const useCreditGateModal = () => {
  const openCreditGateModal = useCreditGateStore(
    (state) => state.openCreditGateModal,
  );
  return { openCreditGateModal };
};
